/**
* Copyright (c) 2025 Bytedance, Inc. and its affiliates.
* SPDX-License-Identifier: Apache-2.0
*/
import * as __WEBPACK_EXTERNAL_MODULE__agent_infra_browser_45fd5ecc__ from "@agent-infra/browser";
import * as __WEBPACK_EXTERNAL_MODULE__agent_infra_shared_02f0a8bf__ from "@agent-infra/shared";
import * as __WEBPACK_EXTERNAL_MODULE__agent_infra_logger_ddae3da2__ from "@agent-infra/logger";
import * as __WEBPACK_EXTERNAL_MODULE__utils_url_mjs_fc6e5753__ from "./utils/url.mjs";
import * as __WEBPACK_EXTERNAL_MODULE__utils_misc_mjs_91ab43aa__ from "./utils/misc.mjs";
import * as __WEBPACK_EXTERNAL_MODULE__engines_index_mjs_73a08501__ from "./engines/index.mjs";
function _define_property(obj, key, value) {
    if (key in obj) Object.defineProperty(obj, key, {
        value: value,
        enumerable: true,
        configurable: true,
        writable: true
    });
    else obj[key] = value;
    return obj;
}
class BrowserSearch {
    async perform(options) {
        this.logger.info('Starting search with options:', options);
        const queries = Array.isArray(options.query) ? options.query : [
            options.query
        ];
        const excludeDomains = options.excludeDomains || [];
        const count = options.count && Math.max(3, Math.floor(options.count / queries.length));
        const engine = options.engine || this.defaultEngine;
        try {
            if (this.isBrowserOpen) this.logger.info('Using existing browser instance');
            else {
                this.logger.info('Launching browser');
                await this.browser.launch(this.config.browserOptions);
                this.isBrowserOpen = true;
            }
            const queue = new __WEBPACK_EXTERNAL_MODULE__agent_infra_shared_02f0a8bf__.PromiseQueue(options.concurrency || 15);
            const visitedUrls = new Set();
            const results = await Promise.all(queries.map((query)=>this.search(this.browser, {
                    query,
                    count,
                    queue,
                    visitedUrls,
                    excludeDomains,
                    truncate: options.truncate,
                    needVisitedUrls: options.needVisitedUrls,
                    engine
                })));
            this.logger.success('Search completed successfully');
            const flattenedResults = results.flat().filter((v)=>null !== v);
            this.logger.info('Search results', flattenedResults);
            return flattenedResults;
        } catch (error) {
            this.logger.error('Search failed:', error);
            throw error;
        } finally{
            if (!options.keepBrowserOpen && this.isBrowserOpen) await this.closeBrowser();
        }
    }
    async closeBrowser() {
        if (this.isBrowserOpen) {
            this.logger.info('Closing browser');
            await this.browser.close();
            this.isBrowserOpen = false;
        }
    }
    async search(browser, options) {
        const searchEngine = (0, __WEBPACK_EXTERNAL_MODULE__engines_index_mjs_73a08501__.getSearchEngine)(options.engine);
        const url = searchEngine.getSearchUrl(options.query, {
            count: options.count,
            excludeDomains: options.excludeDomains
        });
        this.logger.info(`Searching with ${options.engine} engine: ${url}`);
        let links = await browser.evaluateOnNewPage({
            url,
            waitForOptions: {
                waitUntil: 'networkidle2'
            },
            pageFunction: searchEngine.extractSearchResults,
            pageFunctionParams: [],
            beforePageLoad: async (page)=>{
                await (0, __WEBPACK_EXTERNAL_MODULE__utils_misc_mjs_91ab43aa__.interceptRequest)(page);
            },
            afterPageLoad: async (page)=>{
                if (searchEngine.waitForSearchResults) await searchEngine.waitForSearchResults(page);
            }
        });
        this.logger.info('Fetched links:', links);
        links = (null == links ? void 0 : links.filter((link)=>{
            if (options.visitedUrls.has(link.url)) return false;
            options.visitedUrls.add(link.url);
            return !(0, __WEBPACK_EXTERNAL_MODULE__utils_url_mjs_fc6e5753__.shouldSkipDomain)(link.url);
        })) || [];
        if (!links.length) {
            this.logger.info('No valid links found');
            return [];
        }
        const results = await Promise.allSettled(options.needVisitedUrls ? links.map((item)=>options.queue.add(()=>this.visitLink(this.browser, item))) : links.map((item)=>({
                ...item,
                content: item.snippet || ''
            })));
        return results.map((result)=>{
            if ('rejected' === result.status || !result.value) return null;
            return {
                ...result.value,
                content: options.truncate ? result.value.content.slice(0, options.truncate) : result.value.content
            };
        });
    }
    async visitLink(browser, item) {
        try {
            this.logger.info('Visiting link:', item.url);
            const result = await browser.evaluateOnNewPage({
                url: item.url,
                pageFunction: __WEBPACK_EXTERNAL_MODULE__agent_infra_shared_02f0a8bf__.extractPageInformation,
                pageFunctionParams: [
                    __WEBPACK_EXTERNAL_MODULE__agent_infra_shared_02f0a8bf__.READABILITY_SCRIPT
                ],
                beforePageLoad: async (page)=>{
                    await (0, __WEBPACK_EXTERNAL_MODULE__utils_misc_mjs_91ab43aa__.interceptRequest)(page);
                }
            });
            if (result) {
                const content = (0, __WEBPACK_EXTERNAL_MODULE__agent_infra_shared_02f0a8bf__.toMarkdown)(result.content);
                return {
                    ...result,
                    url: item.url,
                    content,
                    snippet: item.snippet
                };
            }
        } catch (e) {
            this.logger.info('Failed to visit link:', e);
        }
    }
    constructor(config = {}){
        _define_property(this, "config", void 0);
        _define_property(this, "logger", void 0);
        _define_property(this, "browser", void 0);
        _define_property(this, "isBrowserOpen", void 0);
        _define_property(this, "defaultEngine", void 0);
        this.config = config;
        this.isBrowserOpen = false;
        this.logger = (null == config ? void 0 : config.logger) ?? __WEBPACK_EXTERNAL_MODULE__agent_infra_logger_ddae3da2__.defaultLogger;
        if (this.config.cdpEndpoint) this.browser = new __WEBPACK_EXTERNAL_MODULE__agent_infra_browser_45fd5ecc__.RemoteBrowser({
            logger: this.logger,
            cdpEndpoint: this.config.cdpEndpoint
        });
        else this.browser = config.browser ?? new __WEBPACK_EXTERNAL_MODULE__agent_infra_browser_45fd5ecc__.LocalBrowser({
            logger: this.logger
        });
        this.defaultEngine = config.defaultEngine ?? 'google';
    }
}
export { BrowserSearch };

//# sourceMappingURL=browser-search.mjs.map